import clsx from "clsx"
import { useEffect, useRef, useState } from "react"

import Tooltip from "@atoms/Tooltip/Tooltip"

import styles from "./TruncatableText.module.scss"
import { TruncatableTextProps } from "./TruncatableText.types"

export default function TruncatableText(props: TruncatableTextProps) {
    const {
        text,
        className,
        tooltipOnTruncate = false,
        tooltipSideOffset = 4,
        tooltipPlacement = "bottom",
        onTruncationChange,
        ...rest
    } = props

    const ref = useRef<HTMLDivElement>(null)
    const [isTruncated, setIsTruncated] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) {
            return
        }

        function check() {
            if (element?.clientWidth) {
                const hasTruncation = element.offsetWidth < element.scrollWidth
                setIsTruncated((prev) => {
                    if (prev !== hasTruncation) {
                        return hasTruncation
                    }

                    return prev
                })
            }
        }

        check()
        const resizeObserver = new ResizeObserver(check)
        resizeObserver.observe(element)
        return () => resizeObserver.disconnect()
    }, [text])

    useEffect(() => {
        if (onTruncationChange) {
            onTruncationChange?.(isTruncated)
        }
    }, [isTruncated, onTruncationChange])

    const content = (
        <div
            ref={ref}
            className={clsx(styles.base, className)}
            data-truncated={isTruncated ? "true" : "false"}
            {...rest}
        >
            {text}
        </div>
    )

    if (!isTruncated || !tooltipOnTruncate) {
        return content
    } else {
        return (
            <Tooltip
                body={text}
                wrapChildren={false}
                sideOffset={tooltipSideOffset}
                defaultPlacement={tooltipPlacement}
            >
                {content}
            </Tooltip>
        )
    }
}
